import { Chip, Typography } from '@mui/material'
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { useSearchParams } from 'blocks/SearchResultsBlock/useSearchParams'
import { translations } from 'i18n/translations'
import { capitalize, omitBy } from 'lodash'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useCallback, useMemo, useState } from 'react'
import styles from './filterChips.module.scss'
import { TRangeFilterValues } from './TRangeFilterValues'
import { TSearchParams } from './TSearchParams'
import { useSearchResultsRouting } from './useSearchResultsRouting'

// TODO Add translations to the chip labels (needs bigger refactoring)
type FilterType = keyof Pick<
  TSearchParams,
  | 'machineType'
  | 'brands'
  | 'model'
  | 'features'
  | 'rangeFilters'
  | 'locations'
  | 'machineSubType'
  | 'baleChamber'
  | 'headerType'
>

type Filter = {
  type: FilterType
  label: string
  value: string
}

const getRangeFilterLabel = (attribute: string, values: TRangeFilterValues) => {
  // TODO If we continue using this after the refactoring, we need to add translations here
  if (values.min === undefined) {
    return `${attribute}: up to ${values.max}`
  } else if (values.max === undefined) {
    return `${attribute}: from ${values.min}`
  }
  return `${attribute}: ${values.min} - ${values.max}`
}

const FilterChips: React.FC = () => {
  const { t } = useTranslation(localeNamespace.common)
  const { gotoSearchResults } = useSearchResultsRouting()
  const { isMobile } = useMediaQuery()
  const searchParams = useSearchParams()
  const [showAll, setShowAll] = useState(false)

  // TODO Once we refactor everything to include the translations, we might move this to a hook
  const allFilters: Filter[] = useMemo(() => {
    // TODO Re-enable machineTypeFilter once the search can handle undefined machineType
    // const machineTypeFilter: Filter[] = searchParams.machineType
    //   ? [
    //       {
    //         type: 'machineType',
    //         label: searchParams.machineType,
    //         value: searchParams.machineType,
    //       },
    //     ]
    //   : []
    const brandsFilter: Filter[] = searchParams.brands.map((brand) => ({
      type: 'brands',
      label: brand,
      value: brand,
    }))
    const modelFilter: Filter[] = searchParams.model
      ? [
          {
            type: 'model',
            label: searchParams.model.name,
            value: searchParams.model.name,
          },
        ]
      : []
    const machineSubTypeFilter: Filter[] = searchParams.machineSubType
      ? [
          {
            type: 'machineSubType',
            label: searchParams.machineSubType,
            value: searchParams.machineSubType,
          },
        ]
      : []
    const baleChamberFilter: Filter[] = searchParams.baleChamber
      ? [
          {
            type: 'baleChamber',
            label: searchParams.baleChamber,
            value: searchParams.baleChamber,
          },
        ]
      : []
    const headerTypeFilter: Filter[] = searchParams.headerType
      ? [
          {
            type: 'headerType',
            label: searchParams.headerType,
            value: searchParams.headerType,
          },
        ]
      : []
    const rangeFilters: Filter[] = Object.entries(
      searchParams.rangeFilters
    ).map(([attribute, values]) => ({
      type: 'rangeFilters',
      label: getRangeFilterLabel(attribute, values),
      value: attribute,
    }))
    const featuresFilter: Filter[] = searchParams.features.map((feature) => ({
      type: 'features',
      label: feature,
      value: feature,
    }))
    const locationsFilter: Filter[] = searchParams.locations.map(
      (location) => ({
        type: 'locations',
        label: location,
        value: location,
      })
    )
    return [
      // TODO Re-enable machineTypeFilter once the search can handle undefined machineType
      //...machineTypeFilter,
      ...brandsFilter,
      ...modelFilter,
      ...machineSubTypeFilter,
      ...baleChamberFilter,
      ...headerTypeFilter,
      ...rangeFilters,
      ...featuresFilter,
      ...locationsFilter,
    ].map((f) => ({
      ...f,
      label: capitalize(f.label.toLowerCase().replaceAll('_', ' ')),
    }))
  }, [searchParams])

  const handleDelete = useCallback(
    (filter: Filter) => {
      switch (filter.type) {
        case 'machineType':
        case 'model':
        case 'machineSubType':
        case 'baleChamber':
        case 'headerType':
          gotoSearchResults(
            {
              ...searchParams,
              [filter.type]: undefined,
            },
            isMobile
          )
          break
        case 'brands':
        case 'features':
        case 'locations':
          gotoSearchResults(
            {
              ...searchParams,
              [filter.type]: searchParams[filter.type].filter(
                (f) => f !== filter.value
              ),
            },
            isMobile
          )
          break
        case 'rangeFilters':
          gotoSearchResults({
            ...searchParams,
            rangeFilters: omitBy(
              searchParams.rangeFilters,
              (_, key) => key === filter.value
            ),
          })
          break
      }
    },
    [gotoSearchResults, isMobile, searchParams]
  )

  const handleShowAll = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault()
      setShowAll((prev) => !prev)
    },
    []
  )

  const visibleFilters = showAll ? allFilters : allFilters.slice(0, 10)
  const amountOfHiddenFilters = allFilters.length - visibleFilters.length

  return (
    <>
      {visibleFilters.length > 0 && (
        <div className={styles.wrapper}>
          {visibleFilters.map((filter, index) => (
            <Chip
              key={`${filter.type}-${index}`}
              label={filter.label}
              component='a'
              variant='outlined'
              onDelete={() => handleDelete(filter)}
            />
          ))}
          {amountOfHiddenFilters > 0 && (
            <Link
              className={styles.showMoreButton}
              href='#'
              onClick={handleShowAll}
            >
              <Typography variant='caption' color='text.primaryLight'>
                {showAll && t(translations.searchResultsChipsShowLess)}
                {!showAll &&
                  t(translations.searchResultsChipsShowMore, {
                    amount: amountOfHiddenFilters,
                  })}
              </Typography>
            </Link>
          )}
        </div>
      )}
    </>
  )
}

export default FilterChips
